/**
 * Scroll the lesson editor main pane so the #ce-... anchor is aligned to the top.
 * Shared across the mounted lesson overview and focused-step routes in EditLesson.
 */

export const resolveLessonScrollRoot = (scrollRoot) =>
  scrollRoot ||
  document.querySelector(".pd-body .pd-main .pd-content-scroll") ||
  null;

export const scrollElementTo = (element, options = {}) => {
  if (!element) return;
  if (typeof element.scrollTo === "function") {
    element.scrollTo(options);
    return;
  }
  const top = typeof options?.top === "number" ? options.top : null;
  if (top != null) {
    element.scrollTop = top;
  }
};

/** Scroll the window (no-op when the shell uses a fixed viewport) and the main lesson pane to the top. */
export const resetLessonEditorPaneScroll = (scrollRoot) => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }
  const pane = resolveLessonScrollRoot(scrollRoot);
  if (pane) {
    scrollElementTo(pane, { top: 0, left: 0, behavior: "auto" });
  }
};

/** Supersedes prior in-flight scroll runs when a new navigation starts (effect cleanup does not cancel them). */
let lessonScrollToCeToken = 0;

/** Call on editor unmount so in-flight scroll chains from the previous page are cancelled. */
export const bumpLessonScrollToCeToken = () => {
  lessonScrollToCeToken += 1;
};

export const scrollCeAnchorIntoLessonPane = (anchorId, scrollRoot, options = {}) => {
  const { block = "start", behavior = "smooth" } = options;
  const id = (anchorId || "").replace(/^#/, "");
  if (!id) return () => {};
  const myToken = ++lessonScrollToCeToken;
  let cancelled = false;
  let attempts = 0;
  let tickTimeoutId = null;
  const maxAttempts = 200;
  const followUpDelaysMs = [80, 240, 500, 900, 1400, 2000];
  const followUpTimeouts = [];

  const isStale = () => cancelled || myToken !== lessonScrollToCeToken;

  const scrollBehaviorForOptions = () => (behavior === "smooth" ? "smooth" : "auto");

  const doScroll = () => {
    if (isStale()) return;
    const root = resolveLessonScrollRoot(scrollRoot);
    const el = document.getElementById(id);
    if (!el || !root) return false;
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const padding = 10;
    const nextTop = root.scrollTop + (elRect.top - rootRect.top) - padding;
    scrollElementTo(root, {
      top: Math.max(0, nextTop),
      behavior: scrollBehaviorForOptions(),
    });
    return true;
  };

  const scheduleFollowUps = () => {
    followUpDelaysMs.forEach((ms) => {
      const t = window.setTimeout(() => {
        if (isStale()) return;
        doScroll();
      }, ms);
      followUpTimeouts.push(t);
    });
  };

  const tick = () => {
    if (isStale()) return;
    const root = resolveLessonScrollRoot(scrollRoot);
    const el = document.getElementById(id);
    if (el && root) {
      doScroll();
      scheduleFollowUps();
      return;
    }
    if (el && !root) {
      el.scrollIntoView({
        behavior: scrollBehaviorForOptions(),
        block,
        inline: "nearest",
      });
      scheduleFollowUps();
      return;
    }
    attempts += 1;
    if (attempts < maxAttempts) {
      tickTimeoutId = window.setTimeout(() => {
        tickTimeoutId = null;
        window.requestAnimationFrame(tick);
      }, 24);
    }
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      tick();
    });
  });
  return () => {
    cancelled = true;
    if (tickTimeoutId != null) {
      window.clearTimeout(tickTimeoutId);
      tickTimeoutId = null;
    }
    followUpTimeouts.forEach((t) => window.clearTimeout(t));
  };
};
